import React, { useState, useEffect } from 'react';
import { X, Check, Link as LinkIcon, AlertCircle } from 'lucide-react';

// No file ever leaves the visitor's browser here — we only ever take a link
// to an image already hosted somewhere else (their own cloud storage, a
// social profile photo, etc.), the same way freeCodeCamp's "Photo URL"
// setting works. That keeps us from having to store everyone's original
// uploads ourselves.
const ImageUrlModal = ({ isOpen, type, error, saving, onCancel, onSave }) => {
  const [url, setUrl] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setPreviewFailed(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isBanner = type === 'banner';
  const trimmed = url.trim();

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-foreground">{isBanner ? 'Set banner image' : 'Set profile picture'}</h3>
          <button onClick={onCancel} className="w-8 h-8 rounded-xl hover:bg-muted flex items-center justify-center border-none bg-transparent cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-bold text-muted-foreground mb-1.5 block">Image URL</label>
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-background">
              <LinkIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <input
                type="url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setPreviewFailed(false); }}
                placeholder="https://example.com/your-photo.jpg"
                className="flex-1 bg-transparent border-none outline-none text-sm text-foreground min-w-0"
                autoFocus
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Paste a public link to an image you already host elsewhere — a cloud drive, an image host, a social profile photo.
            </p>
          </div>

          {trimmed && (
            <div className={`bg-muted overflow-hidden flex items-center justify-center ${isBanner ? 'w-full h-32 rounded-xl' : 'w-24 h-24 rounded-2xl mx-auto'}`}>
              {previewFailed ? (
                <span className="text-[11px] text-muted-foreground text-center px-3">Couldn't load a preview from that link</span>
              ) : (
                <img
                  key={trimmed}
                  src={trimmed}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  onError={() => setPreviewFailed(true)}
                />
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-destructive/10 text-destructive text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-lg bg-muted text-foreground text-sm font-bold hover:bg-muted/70 border-none cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={() => trimmed && onSave(trimmed)}
            disabled={saving || !trimmed}
            className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50 border-none cursor-pointer flex items-center justify-center gap-2"
          >
            {saving ? 'Saving...' : <><Check className="w-4 h-4" /> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageUrlModal;
